import { useApolloClient, useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { CircularProgress, Grid } from '@material-ui/core';
import config from 'config';
import { cache } from 'graph_ql';
import { USER_DETAIL } from 'graph_ql/authentication/queries';
import { useQueryData } from 'hooks/useQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, useAppSelector } from 'redux/store';
import { useStyles } from './style';
import CloseIcon from '@material-ui/icons/Close';
import Div100vh from 'react-div-100vh';
import { FlexBox } from 'component/customize_style_component/styled_box';
import { Text } from 'component/customize_style_component/styled_title';
import { GET_IMAGE_DATA } from 'graph_ql/createStyle/query';
import {
    setRenderRequested as requestRenderCanvas,
    generateDefaultStyleInformation,
    setStyleItemsAndImageJson,
    setSelectedStore,
    setQueryStoreCursor,
} from 'redux/create-style/create-style.slice';
import { ToastType } from 'constants/index';
import { SEARCH_ITEM_CREATED } from 'graph_ql/search/search_item_created';
import { StandardAlert } from 'component/StandardAlert';
import { setShowFilter } from 'redux/filter/filterSlice';
import InfiniteScroll from 'react-infinite-scroll-component';
import StandardPopup from 'component/StandardPopup';
import { getImageJsonObject } from '../../../utils/canvasHelper';
import { CREATE_STYLE, UPDATE_STYLE } from 'graph_ql/createStyle/mutation';
import { PATH } from '../../../routes/constant';
import { useHistory } from 'react-router-dom';
import { debounce, omit } from 'lodash';
import { ITEM_SUGGESTION } from 'graph_ql/search/items_suggestion';
import './style.scss';
import AutoCompleteSearch from 'component/header-filter/auto_complete_input';
import { ItemStyleAccess } from '../AddCreateStyleItem/types';
import { AccessFilterType, StorePlatform } from './type';
import themes from 'styles/themes';
import ItemFilters from './ItemFilters';
import UserType from 'constants/user';
import useFetchStoreItems from './hooks/useFetchStoreItems';
import { CREATE_ITEM } from 'graph_ql/item/mutations';
import { ItemResponse } from 'containers/session_details/type';
import { useVisualViewport } from '../../../utils/useVisaulViewport';
import { ReactComponent as RemoveLogo } from 'assets/svg/Photoroom-badge.svg';

interface IAddOrSelectItemProps {
    handleCloseModal: () => void;
    styleData?: any;
    value?: string;
    handleCompletedProcess: () => void;
}

const DESKTOP_WIDTH = 1024;
const ALGOLIA_HIT_PER_PAGE_DESKTOP = 20;

const AddOrSelectItem = ({ handleCloseModal }: IAddOrSelectItemProps) => {
    const {
        items: styleItems,
        imageJson,
        currentEditingStyleId,
        canvasObject: styleCanvasObject,
        selectedStore,
        queryStoreCursor,
    } = useAppSelector((state) => state.createStyle);
    const windowSize = useVisualViewport();
    const history = useHistory();
    const classes = useStyles();
    const [createStyle] = useMutation(CREATE_STYLE);
    const [updateStyle] = useMutation(UPDATE_STYLE);
    const user: any = useQueryData(USER_DETAIL, null, true, true);
    const me = useSelector((state: RootState) => state.userDetail.me);
    const [listStyle, setListStyle] = useState<any[]>([]);
    const userDetail = useMemo(() => user?.dataQuery?.me, [user]);
    const stylistId = userDetail?.id || me?.id;
    const dispatch = useDispatch();
    const [loadingRemove, setLoadingRemove] = useState(false);
    const client = useApolloClient();
    const [searchInput, setSearchInput] = useState<string>('');
    const [selectedConsumer, setSelectedConsumer] = useState<any>(null);
    const [triggerFilter, setTriggerFilter] = useState(!!selectedStore);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<AccessFilterType | null>(
        selectedStore ? null : AccessFilterType.PRIVATE,
    );
    const [accessFilter, setAccessFilter] = useState<AccessFilterType | null>(
        selectedStore ? null : AccessFilterType.PRIVATE,
    );
    const [modalShow, setModalShow] = useState({
        show: false,
        title: '',
        content: '',
        button1: '',
        button2: '',
        path: '',
        type: '',
        buttonCancel: '',
        closeButton: true,
    });

    const [lastSelectedStore, setLastSelectedStore] = useState<any>(null);
    const [storeItems, pageInfo, storeItemsLoading, lastQuery] = useFetchStoreItems(
        triggerFilter,
        setTriggerFilter,
        selectedStore,
        queryStoreCursor,
        searchInput,
        listStyle,
    );
    const [createItem] = useMutation(CREATE_ITEM);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [prevItems, setPrevItems] = useState<ItemResponse[]>([]);

    useEffect(() => {
        if (storeItems) {
            setListStyle([...storeItems]);
        }
    }, [storeItems]);

    useEffect(() => {
        if (selectedStore && selectedStore.id !== lastSelectedStore?.id) {
            dispatch(setQueryStoreCursor(null));
            setSearchInput('');
            setLastSelectedStore(selectedStore);
            if (selectedStore.platform === StorePlatform.Vogacloset) {
                setTriggerFilter(true);
            }
        }
    }, [dispatch, lastSelectedStore?.id, selectedStore]);

    const searchVariables = useMemo(() => {
        cache.modify({
            fields: {
                items() {
                    return {};
                },
            },
        });
        let index = config.algolia.algolia_prefix + 'item_created';
        let filters = `status:available AND inStock:"TRUE"`;
        if (selectedConsumer) {
            filters += ` AND creator:${selectedConsumer.id}`;
        } else if (selectedStore && selectedStore.useTaffiItems) {
            filters += ` AND storeId:${selectedStore.id}`;
        } else {
            switch (accessFilter) {
            case AccessFilterType.PRIVATE:
                filters += ` AND creator:"${stylistId}"`;
                break;
            case AccessFilterType.PUBLIC:
                filters += ` AND accessLevel: "${ItemStyleAccess.Public}" AND NOT creator:"${stylistId}" AND user.userType:stylist`;
                break;
            case AccessFilterType.ALL:
                filters += ` AND (creator:"${stylistId}" OR accessLevel: "${ItemStyleAccess.Public}") AND user.userType:stylist`;
                break;
            }
        }

        return {
            index,
            query: searchInput,
            filters,
            hit: windowSize.viewport.width >= DESKTOP_WIDTH ? ALGOLIA_HIT_PER_PAGE_DESKTOP : config.algoliaHitPerPage,
            page: 0,
            facets: [
                'title',
                'tags.name',
                'sizes.title',
                'accessLevel',
                'user.userName',
                'user.firstName',
                'user.LastName',
                'storeId',
            ],
        };
        // eslint-disable-next-line
    }, [stylistId, accessFilter, searchInput, selectedStore, triggerFilter]);

    const {
        loading: itemsLoading,
        refetch,
        fetchMore,
        data: items,
    } = useQuery(SEARCH_ITEM_CREATED, {
        variables: searchVariables,
        skip: !!selectedStore && !selectedStore.useTaffiItems,
        onCompleted(response) {
            onGetDataStyleComplete(response);
        },
    });
    const onGetDataStyleComplete = (response) => {
        const { hits } = response.items;
        dispatch(setShowFilter(false));
        setTriggerFilter(false);
        setPrevItems(hits);
        setListStyle(hits.map((h) => omit(h, ['storeProductId'])));
    };

    const getFinalImg = async (image, value) => {
        console.log('getFinalImg', image, value, styleItems);
        const {
            data: { getImageData },
        } = await client.query({
            query: GET_IMAGE_DATA,
            variables: {
                input: {
                    url: image,
                    removeBackground: true,
                    itemId: value.id,
                },
            },
            fetchPolicy: 'network-only',
        });

        if (getImageData && getImageData.imageUrl !== '') {
            if (currentEditingStyleId) {
                let items: any[] = styleItems.map((item) => {
                    return {
                        itemId: item.id,
                        variant: omit(item.variant, '__typename'),
                    };
                });
                items.push({
                    itemId: value.id,
                    variant: {
                        color: '',
                        size: '0',
                    },
                });
                const imagesOnCanvas: any[] = [...imageJson];
                const newImageOnCanvas = await getImageJsonObject(
                    image,
                    getImageData.imageUrl,
                    styleCanvasObject,
                    value.id,
                );
                imagesOnCanvas.push(newImageOnCanvas);
                const itemIdsOnCanvas = imagesOnCanvas.filter((image) => image.itemId).map((image) => image.itemId);
                const itemIdOnItems = items.filter((item) => !!item?.itemId).map((item) => item.itemId);
                items = items.filter((item) => itemIdsOnCanvas.includes(item.itemId));
                const input: any = {
                    id: currentEditingStyleId,
                    items,
                    imageJson: imagesOnCanvas.filter((image) => !image.itemId || itemIdOnItems.includes(image.itemId)),
                };
                if (items.length === 1) {
                    input.images = [getImageData.imageUrl];
                }
                const style = await updateStyle({
                    variables: {
                        input,
                    },
                });
                dispatch(
                    setStyleItemsAndImageJson({
                        items: style.data.updateStyle.items,
                        imageJson: style.data.updateStyle.imageJson,
                    }),
                );
                setLoadingRemove(false);
                handleCloseModal();
            } else {
                const style = await createStyle({
                    variables: {
                        input: {
                            ...generateDefaultStyleInformation(),
                            images: [getImageData.imageUrl],
                            items: [
                                {
                                    itemId: value.id,
                                    variant: {
                                        color: '',
                                        size: '0',
                                    },
                                },
                            ],
                            imageJson: [
                                await getImageJsonObject(image, getImageData.imageUrl, styleCanvasObject, value.id),
                            ],
                        },
                    },
                });
                history.replace(PATH.EDIT_STYLE_CANVAS + '/' + style.data.createStyle.taffiId);
            }
        } else {
            setLoadingRemove(false);
            setModalShow({
                ...modalShow,
                show: true,
                title: 'Something went wrong. ',
                content: 'An error occurred while removing the background of your selected photo. Please try again.',
                button1: 'Okay',
                button2: '',
                path: '',
                type: ToastType.Error,
            });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.type === 'keydown') {
            if (selectedStore) {
                const input = e.suggestion ? e.suggestion : searchInput;
                if (input && input !== lastQuery) {
                    dispatch(setQueryStoreCursor(null));
                }
                setTriggerFilter(true);
            } else {
                setLoading(true);
                refetch({
                    ...searchVariables,
                    query: e.suggestion ? e.suggestion : searchInput,
                }).then((response) => {
                    setLoading(false);
                    const { hits } = response.data.items;
                    setListStyle(hits.map((h) => omit(h, ['storeProductId'])));
                });
            }
        }
    };

    const [suggestions, setSuggestions] = useState<Array<string>>([]);
    const [getSuggestions] = useLazyQuery(ITEM_SUGGESTION, {
        onCompleted(response) {
            const tempHits = [...response.locationsInRadius.hits];
            const tempSuggestions = tempHits.map((item) => item.query);
            setSuggestions(tempSuggestions);
        },
        onError(error) {
            console.error('get data Failed: ', error);
        },
    });

    const handleGetSugestions = (input) => {
        if (!selectedStore) {
            getSuggestions({
                variables: {
                    query: input,
                    // query: 'acces',
                    filters: ``,
                    facets: [],
                },
            });
        }
    };
    const handleSelectItem = async (selectedItem) => {
        let existingItem: any;
        if (selectedItem.hasOwnProperty('platform')) {
            existingItem = styleItems.find((i) => {
                return i.data.hasOwnProperty('storeProductId') && i.data.storeProductId === selectedItem.storeProductId;
            });
        } else {
            existingItem = styleItems.find((i) => {
                return i.id === selectedItem.id;
            });
        }

        if (existingItem) {
            setModalShow({
                ...modalShow,
                show: true,
                title: 'Item already selected',
                content: 'The selected item is already added to the Style. Please select another item',
                button1: 'Okay',
                button2: '',
                path: '',
            });
            return;
        }

        let item = selectedItem;
        if (item.hasOwnProperty('storeProductId')) {
            item = (
                await createItem({
                    variables: {
                        input: { ...omit(selectedItem, ['platform']) },
                    },
                })
            ).data.createItem;
        }

        // check if there are any items from other consumers, if so notify and prevent the addition of items from different consumers.
        const consumerItem = styleItems
            .filter((item) => item != null)
            .find((item) => item.data?.user?.userType === UserType.Customer);
        if (item.user?.userType === UserType.Customer && consumerItem && consumerItem.data.creator !== item.creator) {
            setModalShow({
                ...modalShow,
                show: true,
                title: 'Cannot add item',
                content: `This style already contains item from consumer ${consumerItem.data.user.firstName}. You cannot add item from another consumer.`,
                button1: 'Okay',
                button2: '',
                path: '',
            });
            return;
        }

        setLoadingRemove(true);
        await getFinalImg(selectedItem.images[0], item);
        dispatch(requestRenderCanvas(true));
    };

    const debounceLoadMoreStoreItems = debounce(() => {
        dispatch(setQueryStoreCursor(pageInfo.endCursor));
        setTriggerFilter(true);
    }, 1000);

    const fetchMoreStyles = () => {
        if (!items) {
            if (storeItems && pageInfo) {
                if (pageInfo.hasNextPage) {
                    debounceLoadMoreStoreItems();
                }
                return;
            }
            return;
        }
        const { nbPages } = items?.items;
        if (currentPage >= nbPages - 1) {
            return;
        }
        fetchMore({
            variables: {
                page: currentPage + 1,
            },
            updateQuery: (prev: any, { fetchMoreResult }) => {
                let result;
                setCurrentPage(fetchMoreResult?.items?.page);
                if (!fetchMoreResult || fetchMoreResult?.items?.page <= prev?.items?.page) {
                    result = prev;
                } else {
                    result = {
                        ...prev,
                        items: {
                            hits: [...(prevItems || []), ...(fetchMoreResult?.items?.hits || [])],
                            page: fetchMoreResult?.items?.page,
                            nbPages: fetchMoreResult?.items?.nbPages,
                        },
                    };
                }
                onGetDataStyleComplete(result);
            },
        });
    };

    useEffect(() => {
        handleGetSugestions(searchInput);
        //eslint-disable-next-line
    }, [searchInput]);

    const setSearchItemsHandler = async (filter) => {
        setActiveIndex(filter);
        setSearchInput('');
        if (filter) {
            setSelectedConsumer(null);
            dispatch(setSelectedStore(null));
            setTriggerFilter(true);
        }

        switch (filter) {
        case AccessFilterType.PRIVATE:
            setAccessFilter(AccessFilterType.PRIVATE);
            return;
        case AccessFilterType.PUBLIC:
            setAccessFilter(AccessFilterType.PUBLIC);
            return;
        case AccessFilterType.ALL:
            setAccessFilter(AccessFilterType.ALL);
            return;
        default:
            break;
        }
    };

    return (
        <div className={classes.wrapper}>
            <Grid
                container
                style={{
                    padding: '10px',
                    marginBottom: '-1px',
                    position: 'relative',
                }}
            >
                <Grid
                    item
                    xs={12}
                    xl={12}
                    sm={12}
                    md={12}
                    lg={12}
                    style={{
                        textAlign: 'center',
                        fontFamily: 'Fraunces_120pt_SuperSoft_Wonky-Regular',
                        fontWeight: 400,
                        fontSize: '24px',
                        color: themes.palette.primary.main,
                        position: 'relative',
                    }}
                >
                    Select An Item
                    <CloseIcon
                        style={{
                            color: themes.palette.primary.main,
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translate(0, -50%)',
                        }}
                        onClick={() => handleCloseModal()}
                    />
                </Grid>
            </Grid>
            <AutoCompleteSearch
                setInputSearch={setSearchInput}
                handler={handleKeyDown}
                value={searchInput}
                suggestions={suggestions}
            />
            <ItemFilters
                setSearchInput={setSearchInput}
                activeIndex={activeIndex}
                setSearchItemsHandler={setSearchItemsHandler}
                selectedConsumer={selectedConsumer}
                setSelectedConsumer={setSelectedConsumer}
                setTriggerFilter={setTriggerFilter}
            />
            <Row id={'itemScrollableDiv'} className={classes.items}>
                {Boolean(listStyle.length && !loading && !itemsLoading && !storeItemsLoading) && (
                    <InfiniteScroll
                        dataLength={listStyle.length}
                        className={'row'}
                        next={() => fetchMoreStyles()}
                        hasMore={
                            (!items && pageInfo && pageInfo.hasNextPage) ||
                            (items && currentPage < items.items.nbPages - 1)
                        }
                        scrollableTarget="itemScrollableDiv"
                        loader={
                            <div className={classes.loaderWrapper}>
                                <Spinner animation="border" style={{ width: 20, height: 20 }} />
                            </div>
                        }
                    >
                        {listStyle?.map((item) => (
                            <Col xs={6} sm={4} md={3} key={item.id || item.storeProductId}>
                                <div className={classes.item} onClick={() => handleSelectItem(item)}>
                                    <img
                                        src={item?.images ? item?.images[0] : ''}
                                        alt="item"
                                        className={classes.itemImage}
                                    />
                                    <p className={classes.itemTitle}>{item?.title}</p>
                                    <p className={classes.itemPrice}>
                                        {item?.price} {item?.currency}
                                    </p>
                                </div>
                            </Col>
                        ))}
                    </InfiniteScroll>
                )}
                {(loading || itemsLoading || storeItemsLoading) && (
                    <div className={classes.progressContainer}>
                        <CircularProgress size="2rem" style={{ color: 'white' }} />
                    </div>
                )}
                {Boolean(!listStyle.length && !loading && !itemsLoading && !storeItemsLoading) && (
                    <StandardAlert
                        height="90%"
                        title={'No Items Found'}
                        body={`Please click the '+' button below to add a new item.`}
                    />
                )}
            </Row>
            {loadingRemove && (
                <>
                    <Div100vh
                        style={{
                            top: 0,
                            left: 0,
                            width: '100%',
                            zIndex: 99,
                            position: 'absolute',
                        }}
                        className="background_loading_style"
                    >
                        <FlexBox
                            flexdirection="column"
                            justifycontent="center"
                            alignitems="center"
                            style={{ height: '100%' }}
                        >
                            <Spinner animation="border" style={{ width: 80, height: 80, color: 'white' }} />
                            <Text size="18px" color="white" margin="20px 0 50px 0 ">
                                Adding Item to style...
                            </Text>
                        </FlexBox>
                    </Div100vh>
                    <div style={{ width: '100%', position: 'absolute', zIndex: 100, bottom: 50 }}>
                        <FlexBox
                            flexdirection="column"
                            justifycontent="center"
                            alignitems="center"
                            style={{ height: '100%' }}
                        >
                            <div style={{ background: 'white', width: '170px' }}>
                                <RemoveLogo
                                    onClick={() => window.open('https://www.photoroom.com/', '_blank')}
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            </div>
                        </FlexBox>
                    </div>
                </>
            )}

            <StandardPopup
                show={modalShow.show}
                onHide={() => setModalShow({ ...modalShow, show: false })}
                title={modalShow.title}
                content={modalShow.content}
                button1={modalShow.button1}
                buttonCancel={modalShow.buttonCancel}
                type={modalShow.type as any}
                closeButton={modalShow.closeButton}
            />
        </div>
    );
};

AddOrSelectItem.WhyDidYouRender = true;

export default AddOrSelectItem;
